import { useEffect, useRef, useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import Disclaimer from "../components/Disclaimer";
import { toast } from "sonner";
import { Trash2, Upload, Plus, FileCode } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const CATEGORIE = ["merce","materiali","ricambi","attrezzature","utenze","telefono","carburante","auto","cancelleria","software","rappresentanza","spese_alimentari","altro"];

export default function Movimenti() {
  const { user } = useAuth();
  const [movs, setMovs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rules, setRules] = useState({});
  const [rulesSource, setRulesSource] = useState("defaults");
  const fileRef = useRef();
  const xmlRef = useRef();
  const [xmlOpts, setXmlOpts] = useState({ direction: "acquisto", detraibilita: 100, categoria: "altro" });

  const [form, setForm] = useState({
    data: new Date().toISOString().slice(0,10),
    tipo: "acquisto",
    imponibile: "",
    aliquota_iva: 22,
    detraibilita: 100,
    categoria: "altro",
    descrizione: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/movimenti");
      setMovs(r.data.movimenti);
    } catch { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (user?.sector) {
      api.get(`/regole/${encodeURIComponent(user.sector)}`)
        .then(r => { setRules(r.data.rules || {}); setRulesSource(r.data.source); })
        .catch(() => {});
    }
  }, [user?.sector]);

  // Auto-adjust detraibilita when category changes (only when opening form or changing category)
  const onCategoryChange = (cat) => {
    const pct = rules[cat];
    setForm(prev => ({
      ...prev,
      categoria: cat,
      detraibilita: (prev.tipo === "acquisto" && pct != null) ? pct : prev.detraibilita,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/movimenti", { ...form, imponibile: Number(form.imponibile), aliquota_iva: Number(form.aliquota_iva), detraibilita: Number(form.detraibilita) });
      toast.success("Movimento aggiunto");
      setShowForm(false);
      setForm({ ...form, imponibile: "", descrizione: "" });
      load();
    } catch (ex) { toast.error(formatErr(ex.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare il movimento?")) return;
    try { await api.delete(`/movimenti/${id}`); load(); toast.success("Eliminato"); }
    catch { toast.error("Errore eliminazione"); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/movimenti/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importati ${r.data.inserted} movimenti${r.data.errors?.length ? ` (${r.data.errors.length} errori)` : ""}`);
      load();
    } catch (ex) { toast.error(formatErr(ex.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const importXml = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    const qs = new URLSearchParams({
      direction: xmlOpts.direction,
      detraibilita: String(xmlOpts.detraibilita),
      categoria: xmlOpts.categoria,
    }).toString();
    try {
      const r = await api.post(`/movimenti/import-xml?${qs}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.fatture_processate} fatture · ${r.data.movimenti_inseriti} movimenti importati${r.data.errors?.length ? ` (${r.data.errors.length} errori)` : ""}`);
      load();
    } catch (ex) { toast.error(formatErr(ex.response?.data?.detail)); }
    finally { if (xmlRef.current) xmlRef.current.value = ""; }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap justify-between items-end gap-4 mb-6">
          <div>
            <div className="label-caps">Registro movimenti</div>
            <h1 className="font-serif-display text-4xl text-[#1E3F33] mt-2">Vendite e acquisti</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <label className="btn-lift px-4 py-2 border border-[#EBE5DF] rounded-md hover:bg-[#EBE5DF] flex items-center gap-2 cursor-pointer" data-testid="csv-import-label">
              <Upload size={16}/> Importa CSV
              <input ref={fileRef} type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="csv-import-input"/>
            </label>
            <label className="btn-lift px-4 py-2 border border-[#EBE5DF] rounded-md hover:bg-[#EBE5DF] flex items-center gap-2 cursor-pointer" data-testid="xml-import-label" title="Importa fatture elettroniche XML (SDI)">
              <FileCode size={16}/> Importa XML SDI
              <input ref={xmlRef} type="file" accept=".xml,.zip" multiple onChange={importXml} className="hidden" data-testid="xml-import-input"/>
            </label>
            <button data-testid="movimenti-new" onClick={()=>setShowForm(v=>!v)} className="btn-lift px-4 py-2 bg-[#1E3F33] text-white rounded-md hover:bg-[#152D24] flex items-center gap-2">
              <Plus size={16}/> Nuovo movimento
            </button>
          </div>
        </div>

        <div className="mb-6"><Disclaimer compact /></div>

        <details className="mb-6 text-sm text-[#6B6B6B]">
          <summary className="cursor-pointer text-[#1E3F33]">Guida ai formati di import (CSV & XML)</summary>
          <div className="mt-3 space-y-4">
            <div>
              <div className="font-medium text-[#1E3F33] mb-1">CSV — colonne richieste</div>
              <div className="card-flat p-3 font-mono text-xs">
                data,tipo,imponibile,aliquota_iva,detraibilita,categoria,descrizione<br/>
                2025-01-15,vendita,1200,22,100,merce,Fattura n.1<br/>
                2025-01-16,acquisto,250,22,40,carburante,Rifornimento
              </div>
            </div>
            <div>
              <div className="font-medium text-[#1E3F33] mb-1">XML SDI — opzioni di import</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <select data-testid="xml-direction" value={xmlOpts.direction} onChange={e=>setXmlOpts({...xmlOpts, direction: e.target.value})} className="px-2 py-1 border border-[#EBE5DF] rounded bg-white text-xs">
                  <option value="acquisto">Ricevute (acquisti)</option>
                  <option value="vendita">Emesse (vendite)</option>
                </select>
                <input data-testid="xml-detraibilita" type="number" min="0" max="100" value={xmlOpts.detraibilita} onChange={e=>setXmlOpts({...xmlOpts, detraibilita: Number(e.target.value)})} className="px-2 py-1 border border-[#EBE5DF] rounded bg-white text-xs" placeholder="Detr %"/>
                <select data-testid="xml-categoria" value={xmlOpts.categoria} onChange={e=>setXmlOpts({...xmlOpts, categoria: e.target.value})} className="px-2 py-1 border border-[#EBE5DF] rounded bg-white text-xs">
                  {CATEGORIE.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <p className="text-xs text-[#6B6B6B] mt-2">Accetta più file .xml o un archivio .zip. Ogni DatiRiepilogo diventa un movimento distinto.</p>
            </div>
            <div className="text-xs text-[#6B6B6B] italic pt-2 border-t border-[#EBE5DF]">
              Presto disponibile: accesso automatico alle fatture via SPID e delega Cassetto Fiscale.
            </div>
          </div>
        </details>

        {showForm && (
          <form onSubmit={submit} className="card-flat p-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="movimento-form">
            <div>
              <label className="label-caps block mb-1">Data</label>
              <input type="date" required value={form.data} onChange={e=>setForm({...form, data:e.target.value})} className="w-full px-3 py-2 border border-[#EBE5DF] rounded-md bg-white"/>
            </div>
            <div>
              <label className="label-caps block mb-1">Tipo</label>
              <select data-testid="form-tipo" value={form.tipo} onChange={e=>setForm({...form, tipo:e.target.value})} className="w-full px-3 py-2 border border-[#EBE5DF] rounded-md bg-white">
                <option value="acquisto">Acquisto</option>
                <option value="vendita">Vendita</option>
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1">Categoria</label>
              <select value={form.categoria} onChange={e=>onCategoryChange(e.target.value)} className="w-full px-3 py-2 border border-[#EBE5DF] rounded-md bg-white">
                {CATEGORIE.map(c=><option key={c} value={c}>{c}{rules[c]!=null ? ` · ${rules[c]}%` : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1">Imponibile (€)</label>
              <input data-testid="form-imponibile" type="number" step="0.01" required value={form.imponibile} onChange={e=>setForm({...form, imponibile:e.target.value})} className="w-full px-3 py-2 border border-[#EBE5DF] rounded-md bg-white"/>
            </div>
            <div>
              <label className="label-caps block mb-1">Aliquota IVA (%)</label>
              <select value={form.aliquota_iva} onChange={e=>setForm({...form, aliquota_iva:e.target.value})} className="w-full px-3 py-2 border border-[#EBE5DF] rounded-md bg-white">
                <option value={22}>22%</option><option value={10}>10%</option>
                <option value={5}>5%</option><option value={4}>4%</option>
                <option value={0}>0%</option>
              </select>
            </div>
            <div>
              <label className="label-caps block mb-1">Detraibilità (%)</label>
              <input type="number" min="0" max="100" value={form.detraibilita} onChange={e=>setForm({...form, detraibilita:e.target.value})} className="w-full px-3 py-2 border border-[#EBE5DF] rounded-md bg-white"/>
              <p className="mt-1 text-xs text-[#6B6B6B]">Precompilato secondo {rulesSource === "airtable+defaults" ? "Airtable" : "regole standard"} per {user?.sector}.</p>
            </div>
            <div className="md:col-span-3">
              <label className="label-caps block mb-1">Descrizione (opzionale)</label>
              <input value={form.descrizione} onChange={e=>setForm({...form, descrizione:e.target.value})} className="w-full px-3 py-2 border border-[#EBE5DF] rounded-md bg-white"/>
            </div>
            <div className="md:col-span-3">
              <button data-testid="movimento-submit" type="submit" className="btn-lift px-6 py-2 bg-[#1E3F33] text-white rounded-md hover:bg-[#152D24]">Salva movimento</button>
            </div>
          </form>
        )}

        <div className="card-flat">
          {loading ? (
            <div className="p-8 text-center text-[#6B6B6B]">Caricamento…</div>
          ) : movs.length === 0 ? (
            <div className="p-8 text-center text-[#6B6B6B]">Nessun movimento. Aggiungi il primo o importa un CSV.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#6B6B6B] bg-[#F9F8F6]">
                    <th className="py-3 px-4">Data</th>
                    <th className="py-3 px-4">Tipo</th>
                    <th className="py-3 px-4">Categoria</th>
                    <th className="py-3 px-4">Descrizione</th>
                    <th className="py-3 px-4 text-right">Imponibile</th>
                    <th className="py-3 px-4 text-right">Aliq.</th>
                    <th className="py-3 px-4 text-right">Detr.%</th>
                    <th className="py-3 px-4 text-right">IVA</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {movs.map((m) => (
                    <tr key={m.id} className="border-t border-[#EBE5DF]" data-testid={`row-${m.id}`}>
                      <td className="py-2 px-4">{m.data}</td>
                      <td className="py-2 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${m.tipo==="vendita" ? "bg-[#EBE5DF] text-[#1E3F33]" : "bg-[#FDF3EE] text-[#C25934]"}`}>{m.tipo}</span>
                      </td>
                      <td className="py-2 px-4">{m.categoria}</td>
                      <td className="py-2 px-4 text-[#6B6B6B]">{m.descrizione}</td>
                      <td className="py-2 px-4 text-right">{eur(m.imponibile)}</td>
                      <td className="py-2 px-4 text-right">{m.aliquota_iva}%</td>
                      <td className="py-2 px-4 text-right">{m.detraibilita}%</td>
                      <td className="py-2 px-4 text-right font-medium">{eur(m.tipo==="vendita" ? m.iva_a_debito : m.iva_detraibile)}</td>
                      <td className="py-2 px-4 text-right">
                        <button data-testid={`delete-${m.id}`} onClick={()=>del(m.id)} className="text-[#A83C22] hover:opacity-70"><Trash2 size={16}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
